import React, { createContext, useContext,useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon,
  PlusIcon,
  UserGroupIcon,
  SparklesIcon,
  BanknotesIcon,
  DocumentCheckIcon,
  ChartBarIcon,
  BoltIcon,
  ClockIcon,
  InformationCircleIcon,
  CreditCardIcon,
  DocumentDuplicateIcon,
  ShieldCheckIcon,
  ArrowRightStartOnRectangleIcon,
  NewspaperIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import NotificationCenter from '../components/features/notifications/NotificationCenter';
import EmbeddedSignature from '../components/features/common/EmbeddedSignature';
import ExerciseNoticeModal from '../components/features/exercise/ExerciseNoticeModal';
import DashboardTour from './DashboardTour';
import PreClosingChecklistModal from '../components/features/preClosing/PreClosingChecklistModal';
import CompanyLogo from '../components/common/CompanyLogo';
import FacilityManagement from '../components/features/facility/FacilityManagement';
import ErrorBoundary from '../components/features/common/ErrorBoundary';
import { useChecklist } from '../context/ChecklistContext';
import AllDocumentsList from '../components/allDoumentsList/AllDocumentsList';

interface DashboardProps {
  facilityId: string | null;
}

interface FacilityData {
  companyName: string;
  ticker: string;
  totalCommitment: number;
  availableAmount: number;
  usedAmount: number;
  discountRate: number;
  vwapPercent: number;
  rapidPercent: number;
  expiryDate: string;
  currentSharePrice: number;
  priceChange24h: number;
  priceChangePercent24h: number;

   documentChecklistStatus: 'pending'| 'completed';
  
  // Share metrics
  totalELOCShares: number;
  sharesExercised: number;
  remainingELOCShares: number;
  exchangeCapShares: number;
  remainingExchangeCapShares: number;
  sharesOutstanding: number; // Add total outstanding shares
  
  // Daily eligibility based on volume
  fiveDayAvgVolume: number;
  currentDayVolume: number;
  lastTradePrice: number;
  maxVWAPPurchaseDollar: number;
  maxVWAPPurchaseShares: number;
  maxRapidPurchaseDollar: number;
  maxRapidPurchaseShares: number;
  
  // Settlement estimates
  vwapSettlement?: {
    valuationPeriod: string;
    settlementDate: string;
    tradingDays: number;
    eligibleShares: number;
    eligibleDollarAmount: number;
    limitedBy: string;
    description?: string;
  };
  rapidSettlement?: {
    valuationPeriod: string;
    settlementDate: string;
    tradingDays: number;
    eligibleShares: number;
    eligibleDollarAmount: number;
    limitedBy: string;
    description?: string;
  };
  advanceDrawSettlement?: {
    valuationPeriod: string;
    settlementDate: string;
    tradingDays: number;
  };
  marketStatusDetail?: {
    isOpen: boolean;
    status: string;
    nextOpen?: Date;
    closeTime?: Date;
  };
  
  // Advance draw metrics
  advanceDrawLimit: number;
  advanceDrawUsed: number;
  advanceDrawAvailable: number;
  advanceDrawUtilizationPercent: number;
  
  status: 'Awaiting Signature' |'Signed'| 'Awaiting Registration' | 'Active' | 'Completed';
  registrationDays?: number;
  
  // S1 Documents
  s1Status?: 'not_started' | 'generating' | 'completed' | 'error';
  s1GeneratedAt?: string;
  s1DocumentUrl?: string;
  legalReviewUrl?: string;
  
  // 8K Documents
  eightKStatus?: 'not_started' | 'generating' | 'completed' | 'error';
  eightKGeneratedAt?: string;
  eightKDocumentUrl?: string;
  pressReleaseUrl?: string;
  
  // Market hours status
  marketStatus: 'pre-market' | 'open' | 'after-hours' | 'closed';
  nextMarketOpen?: string;
  lastVolumeUpdate?: string;
}

interface Transaction {
  id: string;
  type: 'VWAP Purchase' | 'Rapid Purchase' | 'Advance Draw';
  status: 'pending' | 'in_valuation' | 'settling' | 'completed' | 'settled' | 'valuation period' | 'settlement period' | 'initiated' | 'sent';
  date: string;
  amount: number;
  shares?: number;
  exercisePrice?: number;
  currentPrice?: number;
  valuationStart?: string;
  valuationEnd?: string;
  settlementEnd?: string;
  volumeAtExercise?: number;
  volumeThreshold?: number;
  currentVolume?: number;
  volumeProgress?: number;
  finalPrice?: number;
  finalShares?: number;
  documents?: {
    exerciseNotice?: string;
    settlementStatement?: string;
    wireConfirmation?: string;
  };
}

const Dashboard: React.FC<DashboardProps> = ({ facilityId }) => {
  const navigate = useNavigate();
const { completionPercentage } = useChecklist();
  const [facilityData, setFacilityData] = useState<FacilityData | null>(null);
  const [activeTransactions, setActiveTransactions] = useState<Transaction[]>([]);
  const [pastTransactions, setPastTransactions] = useState<Transaction[]>([]);
  const [expandedTransactions, setExpandedTransactions] = useState<Set<string>>(new Set());
  // Note: isLoading state is now managed by loadingState - see line ~586
  const [selectedTab, setSelectedTab] = useState<'overview' | 'transactions' | 'allDocuments' | 'settings'>('overview');
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showPreClosingModal, setShowPreClosingModal] = useState(false);
  const [preClosingModalMessage, setPreClosingModalMessage] = useState<string | undefined>();
  const [isInTourMode, setIsInTourMode] = useState(false);

  // Check if this is the user's first visit
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenDashboardTour');
    // Check URL parameter to force show tour for testing
    const urlParams = new URLSearchParams(window.location.search);
    const forceTour = urlParams.get('tour') === 'true';
    
    if (!hasSeenTour || forceTour) {
      setShowTour(true);
    }
  }, []);
  
  const handleTourComplete = () => {
    setShowTour(false);
    setIsInTourMode(false);
    localStorage.setItem('hasSeenDashboardTour', 'true');
    // Navigate to pre-closing checklist
    if (facilityData?.documentChecklistStatus === 'pending') {
      navigate('/pre-closing-checklist');
    }
  };

  useEffect(() => {
    if (completionPercentage === 100 && facilityData && facilityData. documentChecklistStatus !== 'completed') {
      setFacilityData({
        ...facilityData,
         documentChecklistStatus: 'completed',
         status: 'Active' // Automatically update status to Active
      });
    }
  }, [completionPercentage, facilityData]);

  // Check for auth and redirect only once on mount
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!userStr || !token) {
      navigate('/login');
      return;
    }

    const user = JSON.parse(userStr);
    
    // Redirect admin users to admin dashboard
    if (user.role === 'admin') {
      navigate('/admin');
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Logout function to clear session and redirect to login
  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Call logout endpoint to invalidate server-side session
      if (token) {
        await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/auth/logout`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with logout even if API call fails
    }
    
    // Clear all localStorage items
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('hasSeenDashboardTour');
    
    // Redirect to login page
    navigate('/login');
  };

  // Create a stable reference for the facility ID to prevent unnecessary re-renders
  const [currentFacilityId, setCurrentFacilityId] = useState<string | null>(null);
  
  // Enhanced loading state management
  type LoadingState = 'initializing' | 'resolving-facility' | 'loading' | 'error' | 'no-facility' | 'success';
  const [loadingState, setLoadingState] = useState<LoadingState>('initializing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Computed loading state for backward compatibility
  const isLoading = loadingState === 'initializing' || loadingState === 'resolving-facility' || loadingState === 'loading';
  
  // Enhanced facility ID resolution with proper loading states
  useEffect(() => {
    const resolveFacilityId = async () => {
      try {
        setLoadingState('resolving-facility');
        setErrorMessage(null);
        
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          setLoadingState('error');
          setErrorMessage('No user data found. Please log in again.');
          return;
        }
        
        const user = JSON.parse(userStr);
        let targetId = facilityId || user.facilityId;
        
        // Also check URL params if no facilityId found
        if (!targetId) {
          const urlParams = new URLSearchParams(window.location.search);
          targetId = urlParams.get('facilityId');
          if (targetId) {
            console.log('✅ Facility ID from URL params:', targetId);
          }
        }
        
        if (targetId) {
          // Only update if actually different
          if (targetId !== currentFacilityId) {
            console.log('Facility ID changed from', currentFacilityId, 'to', targetId);
            setCurrentFacilityId(targetId);
          }
          // Don't set loading state here - let data fetching effect handle it
        } else {
          // No facility ID found anywhere - user needs to create a facility
          console.log('No facility ID found - showing no facility state');
          setLoadingState('no-facility');
        }
      } catch (error) {
        console.error('Error resolving facility ID:', error);
        setLoadingState('error');
        setErrorMessage('Failed to resolve facility information.');
      }
    };
    
    resolveFacilityId();
  }, [facilityId, currentFacilityId]); // Depend on both but use comparison to prevent loops
  
  // Separate effect for fetching data - only depends on currentFacilityId
  useEffect(() => {
    // Don't fetch if we don't have a facility ID yet
    if (!currentFacilityId) {
      // Don't set loading to false here - let facility resolution handle the state
      return;
    }
    
    // Set loading state when we start fetching
    setLoadingState('loading');
    
    let isSubscribed = true; // Cleanup flag to prevent setting state after unmount
    let retryTimeout: NodeJS.Timeout | null = null;
    let refreshInterval: NodeJS.Timeout | null = null;
    
    const fetchDashboardData = async () => {
      // Don't fetch if component is unmounted
      if (!isSubscribed) return;
      
      try {
        const userStr = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        
        if (!userStr || !token) {
          if (isSubscribed) {
            setLoadingState('error');
            setErrorMessage('Authentication required. Please log in again.');
          }
          return;
        }

        const user = JSON.parse(userStr);
        
        if (user.role === 'admin') {
          if (isSubscribed) {
            setLoadingState('error');
            setErrorMessage('Admin users cannot access this dashboard.');
          }
          return;
        }

        console.log('Fetching dashboard data for facility:', currentFacilityId);

        // Fetch real dashboard data
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/dashboard/${currentFacilityId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (isSubscribed && response.data.success) {
          const data = response.data.data;
          setFacilityData(data);
          
          // Fetch transactions
          const txResponse = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/transactions/facility/${currentFacilityId}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (isSubscribed && txResponse.data.success) {
            const transactions = txResponse.data.data;
            setActiveTransactions(transactions.filter((tx: Transaction) => 
              ['pending', 'in_valuation', 'settling', 'valuation period', 'settlement period', 'initiated', 'sent'].includes(tx.status)
            ));
            setPastTransactions(transactions.filter((tx: Transaction) => 
              tx.status === 'completed' || tx.status === 'settled'
            ));
          }
          
          // Set success state
          if (isSubscribed) {
            setLoadingState('success');
            setErrorMessage(null);
          }
          
          // Only set up refresh interval after successful fetch
          if (!refreshInterval && isSubscribed) {
            refreshInterval = setInterval(() => {
              // Background refresh - don't change loading state unless there's an error
              if (isSubscribed) {
                const refreshData = async () => {
                  try {
                    const userStr = localStorage.getItem('user');
                    const token = localStorage.getItem('token');
                    if (!userStr || !token) return;

                    const response = await axios.get(
                      `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/dashboard/${currentFacilityId}`,
                      {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      }
                    );

                    if (isSubscribed && response.data.success) {
                      const data = response.data.data;
                      setFacilityData(data);
                      
                      // Update transactions silently
                      const txResponse = await axios.get(
                        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/transactions/facility/${currentFacilityId}`,
                        {
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        }
                      );

                      if (isSubscribed && txResponse.data.success) {
                        const transactions = txResponse.data.data;
                        setActiveTransactions(transactions.filter((tx: Transaction) => 
                          ['pending', 'in_valuation', 'settling', 'valuation period', 'settlement period', 'initiated', 'sent'].includes(tx.status)
                        ));
                        setPastTransactions(transactions.filter((tx: Transaction) => 
                          tx.status === 'completed' || tx.status === 'settled'
                        ));
                      }
                    }
                  } catch (error) {
                    // Silent background update failure - don't disrupt UI
                    console.warn('Background refresh failed:', error);
                  }
                };
                refreshData();
              }
            }, 60000); // Refresh every 60 seconds
          }
        } else if (isSubscribed) {
          // API returned success: false or other error
          setLoadingState('error');
          setErrorMessage(response.data.message || 'Failed to load dashboard data');
        }
      } catch (error: any) {
        if (!isSubscribed) return;
        
        console.error('Error fetching dashboard data:', error);
        
        // Clear any existing interval on error
        if (refreshInterval) {
          clearInterval(refreshInterval);
          refreshInterval = null;
        }
        
        // Handle different types of errors
        if (error.response?.status === 404) {
          // Facility not found
          setLoadingState('no-facility');
          setErrorMessage('Facility not found. You may need to create a new facility.');
        } else if (error.response?.status === 401 || error.response?.status === 403) {
          // Authentication error
          setLoadingState('error');
          setErrorMessage('Authentication failed. Please log in again.');
        } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 429) {
          // Backend issues - don't change state, just retry
          console.warn('Backend issue detected, will retry in 60 seconds');
          retryTimeout = setTimeout(() => {
            if (isSubscribed) fetchDashboardData();
          }, 60000);
          return; // Exit without changing loading state
        } else {
          // Other errors
          setLoadingState('error');
          setErrorMessage(error.response?.data?.message || error.message || 'Failed to load dashboard data');
        }
      }
    };

    // Initial fetch
    fetchDashboardData();
    
    // Cleanup function
    return () => {
      isSubscribed = false;
      if (refreshInterval) clearInterval(refreshInterval);
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [currentFacilityId]); // Only re-run when currentFacilityId actually changes

  
useEffect(() => {

  async function updateDashboard() {
    const token = localStorage.getItem('token');
    if (!currentFacilityId || !token || !facilityData) return;

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/dashboard/${currentFacilityId}`,
        facilityData, 
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log("Updated facility:", response.data);
    } catch (error) {
      console.error("Error updating facility:", error);
    }
  }

  updateDashboard();
}, [facilityData, currentFacilityId]);


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };




  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const toggleTransactionExpanded = (id: string) => {
    const newExpanded = new Set(expandedTransactions);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedTransactions(newExpanded);
  };

  const handleBundleDownload = async (transaction: Transaction) => {
    // In a real implementation, this would call an API endpoint to generate a bundled PDF
    // For now, we'll show an alert
    alert('Bundle download feature will be implemented with the document generation service.');
    
    // Example API call:
    // const response = await axios.get(
    //   `${process.env.REACT_APP_API_URL}/api/transactions/${transaction.id}/bundle`,
    //   { responseType: 'blob', headers: { 'Authorization': `Bearer ${token}` } }
    // );
    // const url = window.URL.createObjectURL(new Blob([response.data]));
    // const link = document.createElement('a');
    // link.href = url;
    // link.setAttribute('download', `transaction-${transaction.id}-bundle.pdf`);
    // document.body.appendChild(link);
    // link.click();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in valuation':
      case 'valuation period':
        return 'bg-navy-100 text-navy-800';
      case 'settling':
      case 'settlement period':
        return 'bg-orange-100 text-orange-800';
      case 'pending':
      case 'initiated':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getFacilityStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Awaiting Signature':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Awaiting Registration':
        return 'bg-navy-100 text-navy-800 border-navy-200';
      case 'Completed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Handle different loading states properly
  if (loadingState === 'initializing' || loadingState === 'resolving-facility' || loadingState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-700 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {loadingState === 'resolving-facility' 
              ? 'Resolving facility information...' 
              : 'Loading your dashboard...'
            }
          </p>
        </div>
      </div>
    );
  }

  if (loadingState === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="mb-4">
            <svg className="h-16 w-16 text-red-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Something went wrong
          </h1>
          <p className="text-gray-600 mb-6">
            {errorMessage || 'Unable to load dashboard data'}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 transition-colors"
            >
              Refresh Page
            </button>
            <button
              onClick={() => {
                setLoadingState('resolving-facility');
                setErrorMessage(null);
              }}
              className="w-full px-4 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loadingState === 'no-facility' || !facilityData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4">
            <svg className="h-16 w-16 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            No Facility Found
          </h1>
          <p className="text-gray-600 mb-6">
            {errorMessage || 'No facility data is available. You may need to create a new facility.'}
          </p>
          <button
            onClick={() => navigate('/check-eligibility')}
            className="px-6 py-3 bg-navy-700 text-white rounded-lg hover:bg-navy-800 font-medium"
          >
            Start New Application
          </button>
        </div>
      </div>
    );
  }

  // isLoading is now defined above with loadingState
  
  const utilizationPercent = (facilityData.usedAmount / facilityData.totalCommitment) * 100;

  // Calculate metrics based on actual transaction data with error handling
  const calculateTransactionMetrics = () => {
    try {
      // Safely get transactions with fallback
      const allTransactions = [...(activeTransactions || []), ...(pastTransactions || [])];
      
      // Calculate total shares exercised from all transactions
      const totalSharesExercised = allTransactions.reduce((total, tx) => {
        const shares = typeof tx.shares === 'number' ? tx.shares : 0;
        return total + shares;
      }, 0);
      
      // Calculate total proceeds/amount from all transactions
      const totalAmountExercised = allTransactions.reduce((total, tx) => {
        const amount = typeof tx.amount === 'number' ? tx.amount : 0;
        return total + amount;
      }, 0);
      
      // Safely get facility data with fallbacks
      const totalELOCShares = typeof facilityData?.totalELOCShares === 'number' ? facilityData.totalELOCShares : 0;
      const totalCommitment = typeof facilityData?.totalCommitment === 'number' ? facilityData.totalCommitment : 0;
      const advanceDrawLimit = typeof facilityData?.advanceDrawLimit === 'number' ? facilityData.advanceDrawLimit : totalCommitment * 0.01;
      
      // Calculate remaining shares
      const remainingELOCShares = Math.max(0, totalELOCShares - totalSharesExercised);
      
      // Calculate updated available balance (total commitment - amount used)
      const availableBalance = Math.max(0, totalCommitment - totalAmountExercised);
      
      // Calculate utilization percentage based on actual transactions
      const actualUtilizationPercent = totalCommitment > 0 
        ? Math.min(100, (totalAmountExercised / totalCommitment) * 100)
        : 0;
      
      // Calculate advance draw usage
      const advanceDrawTransactions = allTransactions.filter(tx => tx.type === 'Advance Draw');
      const advanceDrawUsed = advanceDrawTransactions.reduce((total, tx) => {
        const amount = typeof tx.amount === 'number' ? tx.amount : 0;
        return total + amount;
      }, 0);
      const advanceDrawAvailable = Math.max(0, advanceDrawLimit - advanceDrawUsed);
      const advanceDrawUtilizationPercent = advanceDrawLimit > 0 
        ? Math.min(100, (advanceDrawUsed / advanceDrawLimit) * 100)
        : 0;
      
      return {
        totalSharesExercised,
        totalAmountExercised,
        remainingELOCShares,
        availableBalance,
        actualUtilizationPercent,
        advanceDrawUsed,
        advanceDrawAvailable,
        advanceDrawUtilizationPercent
      };
    } catch (error) {
      console.error('Error calculating transaction metrics:', error);
      // Return safe default values
      return {
        totalSharesExercised: 0,
        totalAmountExercised: 0,
        remainingELOCShares: facilityData?.totalELOCShares || 0,
        availableBalance: facilityData?.totalCommitment || 0,
        actualUtilizationPercent: 0,
        advanceDrawUsed: 0,
        advanceDrawAvailable: facilityData?.advanceDrawLimit || 0,
        advanceDrawUtilizationPercent: 0
      };
    }
  };


  // Add this function inside the Dashboard component
const refreshVolumeProgress = async (transactionId: string) => {
  try {
    const token = localStorage.getItem('token');
    
    // Call the API without providing currentVolume - let the backend fetch it
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/transactions/${transactionId}/volume-progress`,
      {}, // Empty body - backend will fetch current volume
      {
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );
    
    if (response.data.success) {
      console.log('Volume progress updated:', response.data.data);
      console.log('Debug info:', response.data.data.debug);
      
      // Refresh the dashboard data
      setLoadingState('loading');
      const currentId = currentFacilityId;
      setCurrentFacilityId(null);
      setTimeout(() => setCurrentFacilityId(currentId), 100);
    }
  } catch (error) {
    console.error('Error refreshing volume progress:', error);
    const errorMessage = error instanceof Error 
      ? error.message 
      : (error as any)?.response?.data?.message || 'Unknown error occurred';
    alert(`Failed to refresh volume progress: ${errorMessage}`);
  }
};
  
  const transactionMetrics = calculateTransactionMetrics();

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
      {/* Pre-Closing Checklist Banner - Show when facility is not active and check list in not completed*/}
      {facilityData.status !== 'Active' && facilityData. documentChecklistStatus !== 'completed' && completionPercentage<100 && (
        <div className="bg-orange-50 border-b-2 border-orange-400">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DocumentCheckIcon className="h-6 w-6 text-orange-600" />
            <div>
          <h3 className="text-lg font-semibold text-orange-900">Complete Your Pre-Closing Checklist</h3>
          <h3 className="text-sm font-semibold text-orange-900">{completionPercentage}% completed</h3><p className="text-sm text-orange-700"></p>
          <p className="text-sm text-orange-700">
            Your facility is not yet active. Please complete all required steps to activate your equity line.
          </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/pre-closing-checklist')}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors"
          >
            Go to Checklist
          </button>
        </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-4">
              <CompanyLogo 
                ticker={facilityData.ticker} 
                companyName={facilityData.companyName} 
                size="xl" 
              />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Company Dashboard
                </h1>
                <p className="text-gray-600 mt-1">
                  {facilityData.companyName} ({facilityData.ticker})
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter userId="1" />
              {/* todos:  reconsider check {(!facilityData || facilityData.status !== 'Active')} */}
              
              {(!facilityData || facilityData.status !== 'Active') && (
                <button 
                  onClick={() => navigate('/new-facility-check')}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <SparklesIcon className="h-5 w-5" />
                  Start New Facility
                </button>
              )}
              <button 
                onClick={() => {
                  // if (facilityData?.status !== 'Active') {
                  //   alert('Please complete your Pre-Closing Checklist before you can utilize the facility.');
                  //   navigate('/pre-closing-checklist');
                  // } else {
                    setShowExerciseModal(true);
                  //}
                }}
                className="px-4 py-2 bg-navy-700 text-white rounded-lg hover:bg-navy-800 flex items-center gap-2"
              >
                <PlusIcon className="h-5 w-5" />
                New Exercise
              </button>
              <button 
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors"
                title="Sign Out"
              >
                <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                Sign Out
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-6 border-t border-gray-200 -mb-px">
            <button
              onClick={() => setSelectedTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'overview'
                  ? 'border-navy-700 text-navy-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setSelectedTab('transactions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'transactions'
                  ? 'border-navy-700 text-navy-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Transactions
            </button>
            <button
              onClick={() => setSelectedTab('allDocuments')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'allDocuments'
                  ? 'border-navy-700 text-navy-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All Documents 
            </button>
            <button
              onClick={() => setSelectedTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'settings'
                  ? 'border-navy-700 text-navy-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Settings
            </button>
          </div>
        </div>
      </div>

      {/* Content based on selected tab */}
      {selectedTab === 'overview' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Market Status Banner */}
          {facilityData.marketStatus !== 'open' && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-center gap-3">
              <ClockIcon className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm font-medium text-orange-800">
                  Market is currently {facilityData.marketStatus}
                </p>
                {facilityData.nextMarketOpen && (
                  <p className="text-xs text-orange-600">
                    Next market open: {new Date(facilityData.nextMarketOpen).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Facility Management Section */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <FacilityManagement 
              currentFacilityId={currentFacilityId || undefined}
              onSelectFacility={(newFacilityId) => {
                // Update the facility ID and reload dashboard data
                window.location.href = `/dashboard?facilityId=${newFacilityId}`;
              }}
            />
          </motion.div> */}

          {/* Stock Price Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{facilityData.ticker}</h3>
                <p className="text-sm text-gray-600">{facilityData.companyName}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">${facilityData.currentSharePrice.toFixed(2)}</p>
                <div className="flex items-center justify-end gap-1">
                  <span className={`text-sm font-medium ${facilityData.priceChange24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {facilityData.priceChange24h >= 0 ? '+' : ''}{facilityData.priceChange24h.toFixed(2)}
                  </span>
                  <span className={`text-sm ${facilityData.priceChange24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ({facilityData.priceChangePercent24h >= 0 ? '+' : ''}{facilityData.priceChangePercent24h.toFixed(2)}%)
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Facility Status Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Active Facility</h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getFacilityStatusColor(facilityData.status)}`}>
                {facilityData.status}
                {facilityData.status === 'Awaiting Registration' && facilityData.registrationDays && (
                  <span className="ml-2">({facilityData.registrationDays} days)</span>
                )}
              </span>
            </div>

            {/* Daily Eligibility Metrics */}
            <div className="mb-6" id="eligibility-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900">Daily Purchase Eligibility</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <InformationCircleIcon className="h-4 w-4" />
                  <span>Based on 5-day average volume: {formatNumber(facilityData.fiveDayAvgVolume)} shares</span>
                </div>
              </div>

              {/* Purchase Eligibility Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* VWAP Purchase Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-navy-100 rounded-lg">
                        <ChartBarIcon className="h-6 w-6 text-navy-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">VWAP Purchase</h4>
                        <p className="text-xs text-gray-500">Daily Maximum Eligibility</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(facilityData.maxVWAPPurchaseDollar || 0)}</p>
                      <p className="text-sm text-gray-600">{formatNumber(facilityData.maxVWAPPurchaseShares || 0)} shares @ ${facilityData.lastTradePrice.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 mt-1">*Estimation using current share price. Final pricing determined upon conclusion of the Valuation Period.</p>
                    </div>
                    {facilityData.vwapSettlement && (
                      <div className="bg-navy-50 rounded-lg p-3 space-y-1">
                        <p className="text-xs font-semibold text-navy-900">If Exercised Today:</p>
                        <p className="text-xs text-navy-800">
                          <span className="font-medium">Valuation Period:</span> {facilityData.vwapSettlement.valuationPeriod}
                        </p>
                        <p className="text-xs text-navy-800">
                          <span className="font-medium">Est. Settlement:</span> {facilityData.vwapSettlement.settlementDate}
                        </p>
                      </div>
                    )}
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500">
                        {facilityData.vwapSettlement?.description || `Lesser of: 10% of commitment or ${(facilityData.vwapPercent * 100).toFixed(0)}% of 5-day avg volume`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Rapid Purchase Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <BoltIcon className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Rapid Purchase</h4>
                        <p className="text-xs text-gray-500">Daily Maximum Eligibility</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(facilityData.maxRapidPurchaseDollar || 0)}</p>
                      <p className="text-sm text-gray-600">{formatNumber(facilityData.maxRapidPurchaseShares || 0)} shares @ ${facilityData.lastTradePrice.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 mt-1">*Estimation using current share price. Final pricing determined upon conclusion of the Valuation Period.</p>
                    </div>
                    {facilityData.rapidSettlement && (
                      <div className="bg-orange-50 rounded-lg p-3 space-y-1">
                        <p className="text-xs font-semibold text-orange-900">If Exercised Today:</p>
                        <p className="text-xs text-orange-800">
                          <span className="font-medium">Settlement:</span> {facilityData.rapidSettlement.settlementDate}
                        </p>
                      </div>
                    )}
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500">
                        {facilityData.rapidSettlement?.description || `Lesser of: 10% of commitment or 20% of 5-day avg volume`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advance Draw Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <BanknotesIcon className="h-6 w-6 text-gray-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Advance Draw Facility</h4>
                      <p className="text-xs text-gray-500">1% of total commitment</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Available Balance</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(transactionMetrics.availableBalance)}</p>
                    <p className="text-xs text-gray-500 mt-1">Immediate funding available</p>
                    {facilityData.advanceDrawSettlement && (
                      <div className="bg-gray-50 rounded-lg p-3 mt-3 space-y-1">
                        <p className="text-xs font-semibold text-gray-900">If Exercised Today:</p>
                        <p className="text-xs text-gray-800">
                          <span className="font-medium">Valuation:</span> {facilityData.advanceDrawSettlement.tradingDays} trading days
                        </p>
                        <p className="text-xs text-gray-800">
                          <span className="font-medium">Est. Settlement:</span> {facilityData.advanceDrawSettlement.settlementDate}
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Current Utilization</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(transactionMetrics.totalAmountExercised)}</p>
                    <div className="mt-2">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Utilized</span>
                        <span>{transactionMetrics.actualUtilizationPercent.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-navy-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${transactionMetrics.actualUtilizationPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exercise Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" id="exercise-notices">
                <button 
                  id="vwap-notice"
                  onClick={() => {
                    // If tour is showing OR in tour mode, just show the exercise modal
                    if (showTour || isInTourMode) {
                      setShowExerciseModal(true);
                      return;
                    }
                    
                    // Normal mode checks
                    if (facilityData.status !== 'Active') {
                      setPreClosingModalMessage('Please complete your Pre-Closing Checklist before you can utilize the VWAP Purchase facility.');
                      setShowPreClosingModal(true);
                    } else if (!facilityData.maxVWAPPurchaseShares || facilityData.maxVWAPPurchaseShares <= 0) {
                      alert('No shares are currently eligible for VWAP Purchase.');
                    } else {
                      setShowExerciseModal(true);
                    }
                  }}
                  className="px-6 py-4 bg-white border-2 border-navy-600 text-navy-600 rounded-lg hover:bg-navy-50 flex items-center justify-center gap-2 transition-colors font-semibold"
                >
                  <ChartBarIcon className="h-5 w-5" />
                  VWAP Purchase
                </button>
                <button 
                  onClick={() => {
                    if (facilityData.status !== 'Active') {
                      setPreClosingModalMessage('Please complete your Pre-Closing Checklist before you can utilize the Rapid Purchase facility.');
                      setShowPreClosingModal(true);
                    } else if (!facilityData.maxRapidPurchaseShares || facilityData.maxRapidPurchaseShares <= 0) {
                      alert('No shares are currently eligible for Rapid Purchase.');
                    } else {
                      setShowExerciseModal(true);
                    }
                  }}
                  className="px-6 py-4 bg-white border-2 border-orange-600 text-orange-600 rounded-lg hover:bg-orange-50 flex items-center justify-center gap-2 transition-colors font-semibold"
                >
                  <BoltIcon className="h-5 w-5" />
                  Rapid Purchase
                </button>
                <button 
                  onClick={() => {
                    if (facilityData.status !== 'Active') {
                      setPreClosingModalMessage('Please complete your Pre-Closing Checklist before you can utilize the Advance Draw facility.');
                      setShowPreClosingModal(true);
                    } else {
                      navigate('/exercise/advance');
                    }
                  }}
                  className="px-6 py-4 bg-white border-2 border-gray-600 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2 transition-colors font-semibold"
                >
                  <BanknotesIcon className="h-5 w-5" />
                  Exercise Advance Draw
                </button>
              </div>
            </div>

            {/* Share Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 pt-6 border-t border-gray-200">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Total ELOC Shares</p>
                <p className="text-xl font-bold text-gray-900">{formatNumber(facilityData.totalELOCShares)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Shares Exercised</p>
                <p className="text-xl font-bold text-orange-600">{formatNumber(transactionMetrics.totalSharesExercised)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Remaining ELOC Shares</p>
                <p className="text-xl font-bold text-green-600">{formatNumber(transactionMetrics.remainingELOCShares)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Exchange Cap Remaining</p>
                <p className="text-xl font-bold text-navy-600">{formatNumber(facilityData.remainingExchangeCapShares)}</p>
                <p className="text-xs text-gray-500 mt-1">of {formatNumber(facilityData.exchangeCapShares)} (19.99%)</p>
              </div>
            </div>

            {/* Facility Info Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <div>
                <p className="text-sm text-gray-500">Total Commitment</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(facilityData.totalCommitment)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Available Amount</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(transactionMetrics.availableBalance)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Discount Rate</p>
                <p className="text-lg font-semibold text-gray-900">{facilityData.discountRate}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Expiry Date</p>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(facilityData.expiryDate).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Facility Utilization Chart */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Facility Utilization</span>
                <span className="text-sm font-medium text-gray-900">{transactionMetrics.actualUtilizationPercent.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${transactionMetrics.actualUtilizationPercent}%` }}
                />
              </div>
            </div>
          </motion.div>

          {/* Signature Status for Deal Documents */}
          {facilityData.status === 'Awaiting Signature' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="mb-6"
            >
              <EmbeddedSignature
                facilityId={currentFacilityId || ''}
                documentType="deal_documents"

                onSigningComplete={() => {
                  console.log('Signing complete callback triggered');
                  
                  // store the current facility data with explicit status type
                  const updatedFacilityData = { ...facilityData, status: "Signed" as "Signed" };
                  // update the status of that data
                  setFacilityData(updatedFacilityData);
                }}
              />
            </motion.div>
          )}


          {/* S1 Documents Section */}
          {facilityData.s1Status === 'completed' && (facilityData.s1DocumentUrl || facilityData.legalReviewUrl) && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.06 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">S-1 Registration Documents</h3>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-700 font-medium">Generated</span>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Generated on {facilityData.s1GeneratedAt ? new Date(facilityData.s1GeneratedAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'Unknown'}
              </p>
              
              <div className="space-y-3">
                {facilityData.s1DocumentUrl && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <DocumentTextIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">S-1 Registration Statement</p>
                        <p className="text-sm text-gray-600">Complete SEC filing document</p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const response = await axios.get(
                            `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${facilityData.s1DocumentUrl}`,
                            {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              },
                              responseType: 'blob'
                            }
                          );
                          
                          const contentType = response.headers['content-type'] || 'application/pdf';
                          const blob = new Blob([response.data], { type: contentType });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          const fileExtension = contentType.includes('pdf') ? 'pdf' : 'html';
                          a.download = `S1_Registration_${facilityData.ticker}_${new Date().toISOString().split('T')[0]}.${fileExtension}`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error) {
                          console.error('Error downloading S1 document:', error);
                          alert('Failed to download document. Please try again.');
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Download PDF
                    </button>
                  </div>
                )}
                
                {facilityData.legalReviewUrl && (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                        <ShieldCheckIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Legal Review Letter</p>
                        <p className="text-sm text-gray-600">Professional review and analysis</p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const response = await axios.get(
                            `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${facilityData.legalReviewUrl}`,
                            {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              },
                              responseType: 'blob'
                            }
                          );
                          
                          const contentType = response.headers['content-type'] || 'application/pdf';
                          const blob = new Blob([response.data], { type: contentType });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          const fileExtension = contentType.includes('pdf') ? 'pdf' : 'html';
                          a.download = `Legal_Review_${facilityData.ticker}_${new Date().toISOString().split('T')[0]}.${fileExtension}`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error) {
                          console.error('Error downloading legal review:', error);
                          alert('Failed to download document. Please try again.');
                        }
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      Download PDF
                    </button>
                  </div>
                )}
              </div>
              
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <InformationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Document Notice</p>
                    <p className="text-xs text-amber-700 mt-1">
                      These documents are generated for informational purposes. Please consult with your legal and financial advisors before filing with the SEC.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* 8K Documents Section */}
          {facilityData.eightKStatus === 'completed' && (facilityData.eightKDocumentUrl || facilityData.pressReleaseUrl) && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.07 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Form 8-K Current Report</h3>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-700 font-medium">Generated</span>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Generated on {facilityData.eightKGeneratedAt ? new Date(facilityData.eightKGeneratedAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'Unknown'}
              </p>
              
              <div className="space-y-3">
                {facilityData.eightKDocumentUrl && (
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                        <DocumentTextIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Form 8-K Current Report</p>
                        <p className="text-sm text-gray-600">SEC filing announcement</p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const response = await axios.get(
                            `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${facilityData.eightKDocumentUrl}`,
                            {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              },
                              responseType: 'blob'
                            }
                          );
                          
                          const contentType = response.headers['content-type'] || 'application/pdf';
                          const blob = new Blob([response.data], { type: contentType });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          const fileExtension = contentType.includes('pdf') ? 'pdf' : 'html';
                          a.download = `8K_Report_${facilityData.ticker}_${new Date().toISOString().split('T')[0]}.${fileExtension}`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error) {
                          console.error('Error downloading 8-K document:', error);
                          alert('Failed to download document. Please try again.');
                        }
                      }}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                    >
                      Download PDF
                    </button>
                  </div>
                )}
                
                {facilityData.pressReleaseUrl && (
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                        <NewspaperIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Press Release</p>
                        <p className="text-sm text-gray-600">Public announcement</p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const response = await axios.get(
                            `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${facilityData.pressReleaseUrl}`,
                            {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              },
                              responseType: 'blob'
                            }
                          );
                          
                          const contentType = response.headers['content-type'] || 'application/pdf';
                          const blob = new Blob([response.data], { type: contentType });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          const fileExtension = contentType.includes('pdf') ? 'pdf' : 'html';
                          a.download = `Press_Release_${facilityData.ticker}_${new Date().toISOString().split('T')[0]}.${fileExtension}`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error) {
                          console.error('Error downloading press release:', error);
                          alert('Failed to download document. Please try again.');
                        }
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                    >
                      Download PDF
                    </button>
                  </div>
                )}
              </div>
              
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <InformationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Document Notice</p>
                    <p className="text-xs text-amber-700 mt-1">
                      These documents are generated for informational purposes. Please consult with your legal and financial advisors before filing with the SEC.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Active Transactions Summary */}
          {activeTransactions.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Transactions</h3>
              <div className="space-y-3">
                {activeTransactions.map(transaction => (
                  <div key={transaction.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                          {transaction.status.replace('_', ' ')}
                        </span>
                        <div>
                          <p className="font-semibold text-gray-900">{transaction.type}</p>
                          <p className="text-sm text-gray-500">
                            {formatNumber(transaction.shares || 0)} shares • {formatCurrency(transaction.amount)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/transactions`)}
                        className="text-navy-600 hover:text-navy-700 text-sm font-medium"
                      >
                        View Details →
                      </button>
                    </div>
                    
                    {/* Volume Progress for VWAP/Rapid */}
                    {(transaction.type === 'VWAP Purchase' || transaction.type === 'Rapid Purchase') && (
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Volume Threshold Progress</span>
                          <span className="font-medium">{transaction.volumeProgress || 0}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              (transaction.volumeProgress || 0) >= 100 ? 'bg-green-600' : 'bg-navy-600'
                            }`}
                            style={{ width: `${Math.min(transaction.volumeProgress || 0, 100)}%` }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                          <div>
                            <span className="font-medium">Volume at Exercise:</span>
                            <br />
                            <span>{formatNumber(transaction.volumeAtExercise || 0)}</span>
                          </div>
                          <div>
                            <span className="font-medium">Current Volume:</span>
                            <br />
                            <span>{formatNumber(transaction.currentVolume || 0)}</span>
                          </div>
                          <div>
                            <span className="font-medium">Volume Since Exercise:</span>
                            <br />
                            <span>{formatNumber((transaction.currentVolume || 0) - (transaction.volumeAtExercise || 0))}</span>
                          </div>
                          <div>
                            <span className="font-medium">10% Threshold:</span>
                            <br />
                            <span>{formatNumber(((transaction.currentVolume || 0) - (transaction.volumeAtExercise || 0)) * 0.1)}</span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-600 bg-navy-50 p-2 rounded">
                          <div className="font-medium mb-1">Volume Threshold Monitoring</div>
                          <div>When 10% of volume since exercise equals or exceeds {formatNumber(transaction.shares || 0)} shares, the Valuation Period concludes immediately.</div>
                          <div className="mt-1 text-gray-500">Updated every 15 minutes during market hours.</div>
                          <button
                            onClick={() => refreshVolumeProgress(transaction.id)}
                            className="mt-2 px-3 py-1 bg-navy-600 text-white text-xs rounded hover:bg-navy-700 transition-colors whitespace-nowrap"
                          >
                            Refresh Now
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Settlement Timeline */}
                    {transaction.settlementEnd && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">
                            <ClockIcon className="inline h-4 w-4 mr-1" />
                            Settlement: {new Date(transaction.settlementEnd).toLocaleDateString()}
                          </span>
                          <button className="text-navy-600 hover:text-navy-700 text-sm font-medium flex items-center gap-1">
                            <CreditCardIcon className="h-4 w-4" />
                            Update Payment
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {selectedTab === 'transactions' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Transaction History</h2>
          
          {/* Active Transactions */}
          {activeTransactions.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Transactions</h3>
              <div className="space-y-4">
                {activeTransactions.map(transaction => (
                  <motion.div
                    key={transaction.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-lg shadow-sm border border-gray-200"
                  >
                    <div
                      className="p-6 cursor-pointer"
                      onClick={() => toggleTransactionExpanded(transaction.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(transaction.status)}`}>
                            {transaction.status}
                          </span>
                          <div>
                            <h4 className="font-semibold text-gray-900">{transaction.type}</h4>
                            <p className="text-sm text-gray-500">
                              Submitted: {new Date(transaction.date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">{formatCurrency(transaction.amount)}</p>
                          {transaction.shares && (
                            <p className="text-sm text-gray-500">{formatNumber(transaction.shares)} shares</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {expandedTransactions.has(transaction.id) && (
                      <div className="px-6 pb-6 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          {transaction.exercisePrice && (
                            <div>
                              <p className="text-sm text-gray-500">Exercise Price</p>
                              <p className="font-medium">${transaction.exercisePrice.toFixed(2)}</p>
                            </div>
                          )}
                          {transaction.valuationEnd && (
                            <div>
                              <p className="text-sm text-gray-500">Valuation End</p>
                              <p className="font-medium">{new Date(transaction.valuationEnd).toLocaleString()}</p>
                            </div>
                          )}
                          {transaction.settlementEnd && (
                            <div>
                              <p className="text-sm text-gray-500">Expected Settlement</p>
                              <p className="font-medium">{new Date(transaction.settlementEnd).toLocaleString()}</p>
                            </div>
                          )}
                          {transaction.volumeProgress !== undefined && (
                            <div>
                              <p className="text-sm text-gray-500">Volume Threshold Progress</p>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${
                                      (transaction.volumeProgress || 0) >= 100 ? 'bg-green-600' : 'bg-navy-600'
                                    }`}
                                    style={{ width: `${Math.min(transaction.volumeProgress || 0, 100)}%` }}
                                  />
                                </div>
                                <span className="text-sm font-medium">{transaction.volumeProgress}%</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mt-2">
                                <div>
                                  <span className="font-medium">Volume at Exercise:</span>
                                  <br />
                                  <span>{formatNumber(transaction.volumeAtExercise || 0)}</span>
                                </div>
                                <div>
                                  <span className="font-medium">Current Volume:</span>
                                  <br />
                                  <span>{formatNumber(transaction.currentVolume || 0)}</span>
                                </div>
                                <div>
                                  <span className="font-medium">Volume Since Exercise:</span>
                                  <br />
                                  <span>{formatNumber((transaction.currentVolume || 0) - (transaction.volumeAtExercise || 0))}</span>
                                </div>
                                <div>
                                  <span className="font-medium">10% Threshold:</span>
                                  <br />
                                  <span>{formatNumber(((transaction.currentVolume || 0) - (transaction.volumeAtExercise || 0)) * 0.1)}</span>
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded mt-2">
                                <div className="font-medium mb-1">Volume Threshold Monitoring</div>
                                <div>When 10% of volume since exercise equals or exceeds {formatNumber(transaction.shares || 0)} shares, the Valuation Period concludes immediately.</div>
                                <div className="mt-1 text-gray-500">Updated every 15 minutes during market hours.</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Past Transactions */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Completed Transactions</h3>
            <div className="space-y-4">
              {pastTransactions.map(transaction => (
                <motion.div
                  key={transaction.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg shadow-sm border border-gray-200"
                >
                  <div
                    className="p-6 cursor-pointer"
                    onClick={() => toggleTransactionExpanded(transaction.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(transaction.status)}`}>
                          {transaction.status}
                        </span>
                        <div>
                          <h4 className="font-semibold text-gray-900">{transaction.type}</h4>
                          <p className="text-sm text-gray-500">
                            Completed: {new Date(transaction.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-900">{formatCurrency(transaction.amount)}</p>
                        {transaction.finalShares && (
                          <p className="text-sm text-gray-500">{formatNumber(transaction.finalShares)} shares</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedTransactions.has(transaction.id) && (
                    <div className="px-6 pb-6 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 mb-3">Transaction Details</h5>
                          <dl className="space-y-2">
                            {transaction.finalPrice && (
                              <div className="flex justify-between text-sm">
                                <dt className="text-gray-600">Final Purchase Price:</dt>
                                <dd className="font-medium">${transaction.finalPrice.toFixed(2)}</dd>
                              </div>
                            )}
                            {transaction.finalShares && (
                              <div className="flex justify-between text-sm">
                                <dt className="text-gray-600">Final Shares:</dt>
                                <dd className="font-medium">{formatNumber(transaction.finalShares)}</dd>
                              </div>
                            )}
                            <div className="flex justify-between text-sm">
                              <dt className="text-gray-600">Total Proceeds:</dt>
                              <dd className="font-medium">{formatCurrency(transaction.amount)}</dd>
                            </div>
                          </dl>
                        </div>
                        
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 mb-3">Documents</h5>
                          <div className="space-y-2">
                            {/* Individual document downloads */}
                            <div className="flex gap-2">
                              {transaction.documents?.exerciseNotice && (
                                <a
                                  href={transaction.documents.exerciseNotice}
                                  className="flex-1 flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100 text-xs"
                                  download
                                >
                                  <DocumentTextIcon className="h-4 w-4 text-gray-600" />
                                  <span>Notice</span>
                                </a>
                              )}
                              {transaction.documents?.settlementStatement && (
                                <a
                                  href={transaction.documents.settlementStatement}
                                  className="flex-1 flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100 text-xs"
                                  download
                                >
                                  <DocumentCheckIcon className="h-4 w-4 text-gray-600" />
                                  <span>Settlement</span>
                                </a>
                              )}
                              {transaction.documents?.wireConfirmation && (
                                <a
                                  href={transaction.documents.wireConfirmation}
                                  className="flex-1 flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100 text-xs"
                                  download
                                >
                                  <BanknotesIcon className="h-4 w-4 text-gray-600" />
                                  <span>Wire</span>
                                </a>
                              )}
                            </div>
                            
                            {/* Bundle download button */}
                            <button
                              onClick={() => handleBundleDownload(transaction)}
                              className="w-full flex items-center justify-center gap-2 p-3 bg-navy-600 text-white rounded-lg hover:bg-navy-700 transition-colors"
                            >
                              <DocumentDuplicateIcon className="h-5 w-5" />
                              <span className="font-medium">Download Complete Bundle</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {selectedTab === 'settings' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Company Settings</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Company Information</h3>
                <button 
                  onClick={() => alert('Company information editing will be available in a future update.')}
                  className="text-navy-600 hover:text-navy-700 text-sm font-medium"
                >
                  Edit
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Company Name</p>
                  <p className="font-medium">{facilityData.companyName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ticker Symbol</p>
                  <p className="font-medium">{facilityData.ticker}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Facility Status</p>
                  <p className="font-medium">{facilityData.status}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
                <button 
                  onClick={() => navigate('/user-management')}
                  className="text-navy-600 hover:text-navy-700 text-sm font-medium flex items-center gap-1"
                >
                  <UserGroupIcon className="h-4 w-4" />
                  Manage Users
                </button>
              </div>
              <p className="text-gray-600">
                Control user access and permissions for your facility.
              </p>
            </motion.div>
          </div>
        </div>
      )}

      {selectedTab === 'allDocuments' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">All Documents</h2>
          {/* Documents list */}
          {/* Document list goes here */}
              <AllDocumentsList facilityId={currentFacilityId ?? undefined} onDocumentAction={(doc) => console.log(doc)} />
        </div>
      )}

      {/* Dashboard Tour */}
      {showTour && (
        <DashboardTour 
          onComplete={handleTourComplete}
          onTourModeChange={setIsInTourMode}
          onTriggerVwapDemo={() => setShowExerciseModal(true)}
        />
      )}

      {/* Pre-Closing Checklist Modal */}
      <PreClosingChecklistModal
        isOpen={showPreClosingModal}
        onClose={() => setShowPreClosingModal(false)}
        message={preClosingModalMessage}
      />

      {/* Exercise Notice Modal */}
      {facilityData && currentFacilityId && (
        <ExerciseNoticeModal
          isOpen={showExerciseModal}
          onClose={() => setShowExerciseModal(false)}
          facilityId={currentFacilityId}
          availableAmount={facilityData.availableAmount}
          currentPrice={facilityData.currentSharePrice}
          ticker={facilityData.ticker}
          companyName={facilityData.companyName}
          vwapPercent={facilityData.vwapPercent}
          rapidPercent={facilityData.rapidPercent}
          commitmentAmount={facilityData.totalCommitment}
          fiveDayAvgVolume={facilityData.fiveDayAvgVolume}
          outstandingShares={facilityData.sharesOutstanding} // Use actual outstanding shares from backend
          isDemoMode={isInTourMode}
          onDemoComplete={handleTourComplete}
          onExerciseSubmitted={() => {
            setShowExerciseModal(false);
            // Refresh dashboard data without full page reload
            setLoadingState('loading');
            // Re-trigger data fetching by incrementing a counter or similar
            const currentId = currentFacilityId;
            setCurrentFacilityId(null);
            setTimeout(() => setCurrentFacilityId(currentId), 100);
          }}
        />
      )}
    </div>
    </ErrorBoundary>
  );
};

export default Dashboard; 